import { useCallback, useMemo, useState } from 'react';

export type SortDir = 'asc' | 'desc';
export type SortType = 'text' | 'number' | 'date';

export interface SortColumn<Row> {
  key: string; // unique column key
  accessor: (row: Row) => string | number | Date | null | undefined;
  type?: SortType; // default 'text'
}

export interface SortState {
  key: string;
  dir: SortDir;
}

function isEmpty(v: string | number | Date | null | undefined): boolean {
  return v === null || v === undefined || v === '';
}

// Type-aware emptiness: a number that doesn't coerce, or a date that doesn't parse,
// is "empty" for sorting purposes just like null/undefined/''. This is the single
// predicate used both by the outer gate (which bypasses the direction multiplier so
// empties always sort last) and by compareValues (which orders among empties).
function isEmptyForType(v: string | number | Date | null | undefined, type: SortType): boolean {
  if (isEmpty(v)) return true;
  if (type === 'number') return Number.isNaN(Number(v));
  if (type === 'date') return Number.isNaN(new Date(v as string | number | Date).getTime());
  return false;
}

function compareValues(
  a: string | number | Date | null | undefined,
  b: string | number | Date | null | undefined,
  type: SortType,
): number {
  const aEmpty = isEmptyForType(a, type);
  const bEmpty = isEmptyForType(b, type);

  // Nulls/undefined/empty-string/unparsable always sort last, regardless of direction.
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;

  if (type === 'number') {
    const an = Number(a);
    const bn = Number(b);
    return an - bn;
  }

  if (type === 'date') {
    const at = new Date(a as string | number | Date).getTime();
    const bt = new Date(b as string | number | Date).getTime();
    return at - bt;
  }

  return String(a).localeCompare(String(b), undefined, { sensitivity: 'base', numeric: true });
}

export function useSortableTable<Row>(
  rows: readonly Row[] | undefined,
  columns: readonly SortColumn<Row>[],
  defaultSort: SortState | null,
): {
  rows: Row[];
  sort: SortState | null;
  toggle: (key: string) => void;
  setSort: (s: SortState | null) => void;
} {
  const [sort, setSort] = useState<SortState | null>(defaultSort);

  const toggle = useCallback((key: string) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' };
      return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
    });
  }, []);

  const sortedRows = useMemo(() => {
    const source = rows ? [...rows] : [];
    if (!sort) return source;

    const column = columns.find((c) => c.key === sort.key);
    if (!column) return source;

    const type = column.type ?? 'text';
    const dirMultiplier = sort.dir === 'asc' ? 1 : -1;

    return source
      .map((row, index) => ({ row, index }))
      .sort((a, b) => {
        const av = column.accessor(a.row);
        const bv = column.accessor(b.row);
        const aEmpty = isEmptyForType(av, type);
        const bEmpty = isEmptyForType(bv, type);

        // Nulls last regardless of direction: don't apply dirMultiplier to the empty-ordering.
        if (aEmpty || bEmpty) {
          const cmp = compareValues(av, bv, type);
          if (cmp !== 0) return cmp;
          return a.index - b.index;
        }

        const cmp = compareValues(av, bv, type) * dirMultiplier;
        if (cmp !== 0) return cmp;
        return a.index - b.index; // stable
      })
      .map((entry) => entry.row);
  }, [rows, columns, sort]);

  return { rows: sortedRows, sort, toggle, setSort };
}
